import { afterNextPaint } from '@tunein/web-utils';
import classNames from 'clsx';
import { createPath } from 'history';
import isString from 'lodash/isString';
import PropTypes from 'prop-types';
import { useContext, useMemo } from 'react';
import { useSelector, useStore } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { api } from 'src/client/api';
import { Link } from 'src/common/components/shared/link/Link';
import RecentSearchesContext from 'src/common/contexts/RecentSearchesContext';
import { selectIsDiscord, selectIsMobile } from 'src/common/selectors/app';
import { selectIsUnifiedEventsContentReportingEnabled } from 'src/common/selectors/config';
import { selectIsNowPlayingDialogOpen } from 'src/common/selectors/dialog';
import { getSelectProfile } from 'src/common/selectors/profiles';
import { selectBreadcrumbId } from 'src/common/selectors/reporting';
import { brazeLogContentCardClick } from 'src/common/utils/braze/brazeHelper';
import getGuideItemPathname from 'src/common/utils/guideItem/getGuideItemPathname';
import url from 'url';
import { closeNowPlayingDialog } from '../../../actions/dialog';
import { logContentCardClick } from '../../../actions/logging';
import { setBreadcrumbId } from '../../../actions/reporting';
import { SponsoredRollupContext } from '../../ads/SponsoredRollupProvider';
import css from './guide-item.module.scss';

/**
 * TODO:
 * Eventually, this should reference GuideItemLinkWithLogger, so we can have more universal
 * logging of inappropriate links.When switching to using GuideItemLinkWithLogger, we can
 * imagine the `guideItemLink` prop will be something like the following:
 *
 *  guideItemLink = (logger) => {
 *    const guideItemUrl = getGuideItemPathnameWithLogger(guideItem, parentGuideItem)(logger);
 *    if (guideItemUrl) {
 *      return {
 *        ...url.parse(guideItemUrl),
 *        state: {
 *          itemToken: get(guideItem, 'itemToken')
 *        },
 *      };
 *    }
 *  return guideItemUrl;
 * }
 */

function isPlainLeftClick(event) {
  return event.button === 0 && !(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);
}

function updateLocation(to, updater) {
  return updater ? updater(to) : to;
}

function getLocation(guideItem, guideItemUrl, augmentGuideItemLink) {
  const location = {
    ...url.parse(guideItemUrl),
    state: { itemToken: guideItem?.itemToken },
  };

  // augmentGuideItemLink is injected by the SponsoredRollupProvider in order
  // to add the categoryId to the location state. See SponsoredRollupProvider
  // for more details.
  return updateLocation(location, augmentGuideItemLink);
}

function GuideItemLink({ id, guideItem, children, style, className, onClick, dataTestId }) {
  const onSearchClick = useContext(RecentSearchesContext);
  const augmentGuideItemLink = useContext(SponsoredRollupContext);
  // The parent profile carries the SEO destination info getGuideItemPathname needs for SEO urls.
  const parentGuideItem = useSelector(getSelectProfile(guideItem?.destinationInfo?.id));
  const isDiscord = useSelector(selectIsDiscord);
  const guideItemUrl = getGuideItemPathname(guideItem, parentGuideItem);
  // Pages render hundreds of these links, and each Redux subscription is one more selector that
  // every dispatch runs, so state only the click handler needs is read when it runs.
  const store = useStore();
  const history = useHistory();
  const actions = useMemo(
    () =>
      bindActionCreators(
        {
          closeNowPlayingDialog,
          logContentCardClick,
          setBreadcrumbId,
        },
        store.dispatch,
      ),
    [store],
  );

  const handleClick = (event, to = null) => {
    const state = store.getState();
    const isMobile = selectIsMobile(state);
    const isNowPlayingOpen = selectIsNowPlayingDialogOpen(state);

    if (selectIsUnifiedEventsContentReportingEnabled(state)) {
      if (!selectBreadcrumbId(state) || (isMobile && isNowPlayingOpen)) {
        // We set the breadcrumbId before 'onClick' is called, so the events which called right after(eg. USER_PLAY_CLICKED, LISTEN_SESSION_STARTED) also pass the breadcrumbId.
        actions.setBreadcrumbId();
      }

      if (guideItem.reporting) {
        api.telemetry.reportContentClicked(guideItem.reporting);
      }
    }

    onClick?.(to, event);
    onSearchClick();

    if (guideItem.brazeCardId) {
      brazeLogContentCardClick(guideItem, actions);
    }

    if (isMobile && isNowPlayingOpen && !guideItem.isPlayable) {
      actions.closeNowPlayingDialog();
    }
  };

  const handleLinkClick = async (event, to) => {
    handleClick(event, to);

    if (event.defaultPrevented || !isPlainLeftClick(event)) {
      return;
    }

    // The router link would navigate inside the click, rendering the next route before the browser
    // can paint any response to the tap. Navigate once that paint is done instead.
    event.preventDefault();
    await afterNextPaint();

    if (createPath(history.location) === createPath(to)) {
      history.replace(to);
    } else {
      history.push(to);
    }
  };

  if (isString(guideItemUrl)) {
    const location = getLocation(guideItem, guideItemUrl, augmentGuideItemLink);

    return isDiscord && onClick ? (
      <div
        data-testid={dataTestId}
        style={style}
        className={className}
        onClick={(event) => handleClick(event, location)}
      >
        {children}
      </div>
    ) : (
      <Link
        data-testid={dataTestId}
        to={location}
        style={style}
        className={classNames([className, css.guideItemLink])}
        id={id}
        onClick={(event) => handleLinkClick(event, location)}
      >
        {children}
      </Link>
    );
  }

  return (
    <div
      data-testid={dataTestId}
      style={style}
      className={className}
      onClick={(event) => handleClick(event)}
    >
      {children}
    </div>
  );
}

GuideItemLink.propTypes = {
  id: PropTypes.string,
  children: PropTypes.node.isRequired,
  style: PropTypes.object,
  className: PropTypes.string,
  guideItem: PropTypes.object.isRequired,
  onClick: PropTypes.func,
  dataTestId: PropTypes.string,
};

export default GuideItemLink;
